/* [original source](https://ui.shadcn.com/docs/components/accordion) */

import * as React from 'react'
import { Accordion as AccordionPrimitive } from 'radix-ui'

import { cn, focusRingClassName, tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import { RotatingChevron } from './Button'
import type { ReactNode } from 'react'

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never

export type AccordionProps = DistributiveOmit<
  React.ComponentProps<typeof AccordionPrimitive.Root>,
  'className'
>

function Accordion(props: AccordionProps) {
  switch (props.type) {
    case 'multiple':
      return <AccordionPrimitive.Root {...props} />
    case 'single':
      return <AccordionPrimitive.Root {...props} />
    default: {
      const exhaustiveCheck: never = props
      return exhaustiveCheck
    }
  }
}

const accordionItemVariants = tv({
  base: 'overflow-hidden',

  defaultVariants: {
    borderVariant: 'bordered',
    roundingVariant: 'ends',
    variant: 'default',
  },

  variants: {
    borderVariant: {
      bordered: 'border-x border-t last-of-type:border-b',
      dashed: 'border-x border-t border-dashed last-of-type:border-b',
      divided: 'border-tremor-border border-t first-of-type:border-t-0',
      none: null,
      top: 'border-t',
    },

    overflow: {
      visible: 'overflow-visible',
    },

    roundingVariant: {
      ends: 'first-of-type:rounded-t-lg last-of-type:rounded-b-lg',
      none: null,
      rounded: 'rounded-t-lg rounded-b-lg',
    },

    variant: {
      default: null,
      muted: 'bg-tremor-background-muted',
    },
  },
})

export type AccordionItemProps = Omit<
  React.ComponentProps<typeof AccordionPrimitive.Item>,
  'className'
> &
  VariantProps<typeof accordionItemVariants>

function AccordionItem({
  borderVariant,
  overflow,
  roundingVariant,
  variant,
  ...props
}: AccordionItemProps) {
  return (
    <AccordionPrimitive.Item
      className={accordionItemVariants({
        borderVariant,
        overflow,
        roundingVariant,
        variant,
      })}
      {...props}
    />
  )
}

const RawAccordionItem = AccordionPrimitive.Item

export const accordionTriggerVariants = tv({
  base: [
    'group/accordion group/accordion-trigger flex min-w-0 cursor-pointer items-center text-start transition [&[data-state=open]>[data-slot=rotating-chevron]]:rotate-180',
    focusRingClassName,
    'outline-offset-0',
  ],

  compoundVariants: [
    {
      className: 'px-0',
      paddingX: 'none',
    },
    {
      className: 'px-2',
      paddingX: 'sm',
    },
  ],

  defaultVariants: {
    layout: 'full',
    size: 'md',
    variant: 'default',
  },

  variants: {
    layout: {
      full: 'flex-1 justify-between gap-x-3',
      inline:
        'w-fit gap-x-1.5 rounded-sm [&>[data-slot=rotating-chevron]]:-rotate-90 [&[data-state=open]>[data-slot=rotating-chevron]]:rotate-0!',
    },

    paddingX: {
      none: null,
      sm: null,
    },

    size: {
      md: 'px-5 py-3 text-sm',
      sm: 'px-2.5 py-1.5 text-xs',
      xs: 'rounded-sm px-1.5 py-1 text-xs',
    },

    variant: {
      default:
        'enabled:hover:bg-tremor-background-subtle data-[state=open]:bg-tremor-background-subtle',
      error:
        'border-l-4 border-red-600 bg-red-50 hover:bg-red-100 data-[state=open]:bg-red-100 dark:bg-red-900/50 dark:hover:bg-red-900 dark:data-[state=open]:bg-red-900',
      ghost: 'enabled:hover:bg-tremor-background-subtle',
      info: 'border-l-4 border-blue-600 bg-blue-50 hover:bg-blue-100 data-[state=open]:bg-blue-100 dark:bg-blue-900/50 dark:hover:bg-blue-900 dark:data-[state=open]:bg-blue-900',
      muted: 'bg-tremor-background-muted',
      plain: null,
      subtle: 'bg-tremor-background-subtle',
      success:
        'border-l-4 border-green-600 bg-green-50 hover:bg-green-100 data-[state=open]:bg-green-100 dark:bg-green-900/50 dark:hover:bg-green-900 dark:data-[state=open]:bg-green-900',
      warning:
        'border-l-4 border-yellow-500 bg-tremor-brand-faint hover:bg-tremor-brand-muted data-[state=open]:bg-tremor-brand-muted',
    },
  },
})

export interface AccordionTriggerProps
  extends
    Omit<React.ComponentProps<typeof AccordionPrimitive.Trigger>, 'className'>,
    VariantProps<typeof accordionTriggerVariants> {
  /**
   * @default 'end'
   */
  chevronPosition?: 'start' | 'end'
  /**
   * Rendered as a sibling of the trigger &lt;button&gt; (inside the accordion
   * header), to the right of the chevron. Use this for secondary actions such
   * as overflow menus that must remain clickable without toggling the
   * accordion — nesting them inside `{children}` would produce a &lt;button&gt;
   * inside a &lt;button&gt;, which is invalid HTML and breaks hydration.
   */
  endAdornment?: ReactNode
}

function AccordionTrigger({
  chevronPosition = 'end',
  children,
  endAdornment,
  layout,
  paddingX,
  ref,
  size,
  variant,
  ...props
}: AccordionTriggerProps) {
  const chevron = props.disabled ? null : (
    <RotatingChevron className="text-tremor-content group-disabled/accordion-trigger:hidden" />
  )

  return (
    <AccordionPrimitive.Header
      className={cn(
        'flex w-full min-w-0 items-stretch overflow-hidden',
        endAdornment !== undefined ? 'gap-1' : null,
      )}
    >
      <AccordionPrimitive.Trigger
        ref={ref}
        className={accordionTriggerVariants({
          layout,
          paddingX,
          size,
          variant,
        })}
        {...props}
      >
        {chevronPosition === 'start' ? chevron : null}

        {children}

        {chevronPosition === 'end' ? chevron : null}
      </AccordionPrimitive.Trigger>
      {endAdornment}
    </AccordionPrimitive.Header>
  )
}

export const accordionContentVariants = tv({
  variants: {
    padding: {
      compact: 'px-2.5 py-3',
      none: 'p-0',
      padded: 'px-5 py-4',
    },
    size: {
      md: 'px-5 pt-0 pb-4',
      sm: 'px-2.5 py-2.5',
    },
  },
})

export type AccordionContentProps = Omit<
  React.ComponentProps<typeof AccordionPrimitive.Content>,
  'className'
> &
  VariantProps<typeof accordionContentVariants>

function AccordionContent({
  children,
  padding,
  size = 'md',
  ...props
}: AccordionContentProps) {
  return (
    <AccordionPrimitive.Content
      className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden pb-0 text-sm"
      {...props}
    >
      <div
        className={accordionContentVariants(padding ? { padding } : { size })}
      >
        {children}
      </div>
    </AccordionPrimitive.Content>
  )
}

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  RawAccordionItem,
}
